import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Collapse,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useCase,
  useCaseRequests,
  type CaseRequestFilters,
  type ClientApiRequest,
} from '../api/cases';
import { CaseStatusPill } from '../components/case-status-pill';

function StatusBadge({ status }: { status: number }) {
  const color = status >= 500 ? 'red' : status >= 400 ? 'orange' : 'green';
  return (
    <Badge color={color} variant="light">
      {status}
    </Badge>
  );
}

/** Locale date-time including milliseconds (the timestamp column is DATETIME(3)). */
function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
}

/** Compact header summarising the case these requests belong to. */
function CaseInfoHeader({ id }: { id: string }) {
  const query = useCase(id);
  if (!query.data) return null;
  const c = query.data;
  return (
    <Card withBorder padding="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4}>
          <Group gap="sm">
            <Text fw={600}>
              {c.patient_given_name} {c.patient_family_name}
            </Text>
            <CaseStatusPill caseId={c.id} status={c.status} />
          </Group>
          <Text size="sm" c="dimmed">
            {c.org_name} · {c.product_display_name}
          </Text>
        </Stack>
        <Stack gap={4} align="flex-end">
          <Text size="xs" c="dimmed">
            Case <Code>{c.id}</Code>
          </Text>
          <Text size="xs" c="dimmed">
            Opened {formatTimestamp(c.opened_at)}
          </Text>
        </Stack>
      </Group>
    </Card>
  );
}

// Wrap long JSON / URLs instead of letting them stretch the (fixed-layout) table.
const CODE_BLOCK_STYLE = { whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const };

function RequestRow({ r }: { r: ClientApiRequest }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Table.Tr style={{ cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <Table.Td>
          <ActionIcon variant="subtle" size="sm" aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </ActionIcon>
        </Table.Td>
        <Table.Td>
          <Badge variant="outline">{r.method}</Badge>
        </Table.Td>
        <Table.Td>
          <Code style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>{r.path}</Code>
        </Table.Td>
        <Table.Td>
          <StatusBadge status={r.status} />
        </Table.Td>
        <Table.Td>{formatTimestamp(r.timestamp)}</Table.Td>
        <Table.Td>{r.duration_ms}ms</Table.Td>
      </Table.Tr>
      <Table.Tr>
        <Table.Td colSpan={6} p={0}>
          <Collapse in={open}>
            <Stack gap="xs" p="md">
              <Text size="sm" fw={600}>
                Headers
              </Text>
              <Code block style={CODE_BLOCK_STYLE}>
                {JSON.stringify(r.headers, null, 2)}
              </Code>
              <Text size="sm" fw={600}>
                Payload
              </Text>
              <Code block style={CODE_BLOCK_STYLE}>
                {r.body ? JSON.stringify(r.body, null, 2) : '(no body)'}
              </Code>
              <Text size="sm" fw={600}>
                Response
              </Text>
              <Code block style={CODE_BLOCK_STYLE}>
                {r.response_body ? JSON.stringify(r.response_body, null, 2) : '(no body)'}
              </Code>
            </Stack>
          </Collapse>
        </Table.Td>
      </Table.Tr>
    </>
  );
}

function Section({
  title,
  description,
  rows,
}: {
  title: string;
  description: string;
  rows: ClientApiRequest[];
}) {
  return (
    <Stack gap="sm">
      <div>
        <Title order={5}>{title}</Title>
        <Text size="sm" c="dimmed">
          {description}
        </Text>
      </div>
      {rows.length === 0 ? (
        <Text c="dimmed">None.</Text>
      ) : (
        // Fixed layout + full width so both sections' tables render at identical
        // widths regardless of their content (URL/payload lengths differ per section).
        <Table withTableBorder style={{ tableLayout: 'fixed', width: '100%' }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 44 }} />
              <Table.Th style={{ width: 96 }}>Method</Table.Th>
              <Table.Th>URL</Table.Th>
              <Table.Th style={{ width: 92 }}>Status</Table.Th>
              <Table.Th style={{ width: 210 }}>Time</Table.Th>
              <Table.Th style={{ width: 96 }}>Duration</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <RequestRow key={r.id} r={r} />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

export function CaseRequestsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [filters, setFilters] = useState<CaseRequestFilters>({});
  const query = useCaseRequests(id, filters);

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Client API Requests</Title>
        <Anchor component={Link} to={`/cases/${id}`}>
          Back to case
        </Anchor>
      </Group>

      <CaseInfoHeader id={id} />

      <Group>
        <Select
          placeholder="Method"
          clearable
          data={['GET', 'POST', 'PATCH', 'PUT', 'DELETE']}
          value={filters.method ?? null}
          onChange={(v) => setFilters((f) => ({ ...f, method: v ?? undefined }))}
        />
        <TextInput
          placeholder="Filter by URL"
          value={filters.q ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.currentTarget.value || undefined }))}
        />
      </Group>

      {query.isError ? (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Could not load requests.
        </Alert>
      ) : query.isPending ? (
        <Text c="dimmed">Loading…</Text>
      ) : (
        <>
          {query.data.truncated && (
            <Alert color="yellow">Results truncated to the most recent 500 per section.</Alert>
          )}
          <Section
            title="Case requests"
            description="Requests that acted directly on this case — e.g. creating the case, adding findings or images, and triggering AI review."
            rows={query.data.case_requests}
          />
          <Section
            title="Client session context"
            description="Other requests from the same client around this time that aren't tied to a specific case — e.g. authentication and patient lookups."
            rows={query.data.client_session}
          />
        </>
      )}
    </Stack>
  );
}
